/**
 * repair system — v0M9 (2026-07-27) per MECHANICS.md §2 v0M9 amendment.
 *
 * Ticks the repair timer on every minor Settlement whose `repairState`
 * is non-null. Pauses (does NOT increment) while any hostile army is
 * in the minor's ZoC — matches Michael's design lock: "greyed out if
 * enemy present in ZoC; paused if enemy enters mid-repair". On
 * completion (`daysElapsed >= MINOR_SETTLEMENT_REPAIR_DAYS`): clears
 * the pillaged flag + the repairState.
 *
 * Runs after pillages in the fixed system order — an enemy who just
 * opened a fresh pillage timer this tick will freeze the repair timer
 * on that same tick. Runs before economy so a repair completing this
 * tick lets structure income flow again this tick.
 *
 * Notes:
 *   - No Gold refund on cancel (see `cancel_repair_settlement` in
 *     apply-commands.ts + Michael's design lock).
 *   - Pillage-completing-mid-repair invalidates the repair (pillages.ts
 *     clears repairState on pillage-completion, so this system won't
 *     see a stranded repair against a still-pillaged settlement).
 */

import type { Command } from './commands';
import type { GameState, Settlement } from './state';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import {
  sortedIds,
  TICKS_PER_GAME_DAY,
  MINOR_SETTLEMENT_REPAIR_DAYS,
} from './state';
import { getDialNumber, loadDials } from '../data/dials';

// ---------------------------------------------------------------------------
// Dials
// ---------------------------------------------------------------------------

const REPAIR_DIALS = loadDials();

function safeDialNumber(name: string, fallback: number): number {
  try { return getDialNumber(REPAIR_DIALS, name); } catch { return fallback; }
}

const REPAIR_DAYS = safeDialNumber('minor_settlement_repair_days', MINOR_SETTLEMENT_REPAIR_DAYS);
const REPAIR_ZOC_RADIUS = safeDialNumber('settlement_zoc_radius', 2) + 0.5;

const DAYS_PER_TICK = 1 / TICKS_PER_GAME_DAY;

// ---------------------------------------------------------------------------
// System entry
// ---------------------------------------------------------------------------

export function repairSystem(
  state: GameState,
  _commands: readonly Command[],
  _rng: RngState,
  _context: SimContext,
): GameState {
  const settlementIds = sortedIds(state.settlements);
  for (const sid of settlementIds) {
    const settlement = state.settlements.get(sid);
    if (!settlement) continue;
    if (!settlement.isMinor) continue;
    const r = settlement.repairState;
    if (r == null) continue;

    // Defensive: if the pillaged flag got cleared some other way, tear
    // down the repair too (nothing to repair).
    if (!settlement.pillaged) {
      settlement.repairState = null;
      continue;
    }

    // Pause if any hostile army is in ZoC.
    if (hostileInMinorZoc(state, settlement)) continue;

    r.daysElapsed += DAYS_PER_TICK;
    if (r.daysElapsed >= REPAIR_DAYS) {
      settlement.pillaged = false;
      settlement.repairState = null;
    }
  }
  return state;
}

// ---------------------------------------------------------------------------
// Helper — mirrors pillages.ts / apply-commands.ts ZoC check
// ---------------------------------------------------------------------------

function hostileInMinorZoc(state: GameState, settlement: Settlement): boolean {
  const province = state.provinces.get(settlement.provinceId);
  if (!province) return false;
  const ownerFactionId = province.ownerFactionId;
  let ownerTeam = -Infinity;
  if (ownerFactionId !== null) {
    const f = state.factions.get(ownerFactionId);
    if (!f) return false;
    ownerTeam = f.teamId;
  }
  for (const army of state.armies.values()) {
    if (army.currentBattleId !== null) continue;
    const af = state.factions.get(army.ownerFactionId);
    if (!af) continue;
    if (ownerFactionId !== null && af.teamId === ownerTeam) continue;
    const dx = Math.abs(army.position.x - settlement.cell.x);
    const dy = Math.abs(army.position.y - settlement.cell.y);
    if (Math.max(dx, dy) <= REPAIR_ZOC_RADIUS) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Internals (tests)
// ---------------------------------------------------------------------------

export const _repairInternals = {
  REPAIR_DAYS,
  REPAIR_ZOC_RADIUS,
  DAYS_PER_TICK,
};
